import { cn } from '@app/utils/cn';
import { IconComponent, Notifications } from '@typings';
import { useTranslation } from 'react-i18next';

import { CancelSmall, InfoOutline, SuccessCircleOutline, WarningOutline } from '../Icons';

const ICONS_PER_TYPE = {
  error: WarningOutline,
  info: InfoOutline,
  success: SuccessCircleOutline,
  warning: WarningOutline,
};

interface Props {
  type: Notifications.NotificationType;
  role?: Notifications.Role;
  isFluid?: boolean;
  dataTestId?: string;
  children: React.ReactNode;
  className?: string;
  isClosable?: boolean;
  icon?: IconComponent;
  onClose?: () => void;
  isBordered?: boolean;
}

export const NotificationWrapper = ({
  className,
  type,
  role,
  icon: Icon = ICONS_PER_TYPE[type],
  isClosable = false,
  isFluid = true,
  dataTestId,
  children,
  onClose,
  isBordered = false,
}: Props) => {
  const { t } = useTranslation(['common']);

  const isError = type === 'error';
  const isSuccess = type === 'success';
  const isWarning = type === 'warning';
  const isInfo = type === 'info';

  return (
    <div
      className={cn('relative flex gap-2 overflow-hidden rounded-regular bg-white px-4 py-[10px]', className, {
        'border border-neutral-96': isBordered,
        'pr-9': isClosable,
        'w-full': isFluid,
      })}
      data-testid={dataTestId}
    >
      <div
        className={cn('absolute bottom-0 left-0 top-0 w-1', {
          'bg-error-45': isError,
          'bg-info-62': isInfo,
          'bg-success-47': isSuccess,
          'bg-warning-48': isWarning,
        })}
      />
      <Icon
        className={cn('mt-0.5 size-4 shrink-0 basis-4', {
          'text-error-45': isError,
          'text-info-62': isInfo,
          'text-success-47': isSuccess,
          'text-warning-48': isWarning,
        })}
      />
      <div className="-m-1 flex flex-col overflow-hidden p-1" role={role}>
        {children}
      </div>
      {isClosable && (
        <button
          type="button"
          aria-label={t('common:close')}
          className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full"
          onClick={onClose}
        >
          <CancelSmall />
        </button>
      )}
    </div>
  );
};
